// CDiscardAnnounce — 捨牌「門前預告」
// 打牌時，把要打出的牌以原版 FrontUpEnlargeTileSprite 的正立放大樣式，
// 顯示在靠近該家手牌的門前位置。一般預告會停留後淡出；
// 等待吃／碰／槓／胡時則持續顯示，直到反應結算。
//
// - 位置：DISCARD_ANNOUNCE_PRESENTATIONS（本家上／右家左／對家下／左家右）
// - 動畫：停留 0.3s → 淡出 0.3s → 銷毀並交給海底（原版 DiscardModifier）
// - 每家最多同時一個；同家短時間再次觸發會取代舊的
//
// === 使用圖片 ===
// 透過 CTileSprite (face=TileFace.HandBottom) 渲染（tile-blank-front.png + tiles-source.png；見 tile-sprite.ts）

import type Phaser from 'phaser';

import type { TTile } from '@core/tile';
import type { TSeat } from '@core/types';

import { discardAnnouncePresentationForSeat } from './discard-announce-layout';
import { CTileSprite, TileFace } from './tile-sprite';
import { UIDepth } from './ui-depths';

// ---- 版面 / 動畫常數 ----
const ANNOUNCE_SCALE = 1.0; // 正立放大牌縮放（90×144，明顯大於海底 63×99）
const ANNOUNCE_DEPTH = UIDepth.DiscardAnnounce; // 高於牌面 / 徽章 / 回合手勢(70)，略低於 action icon(80)
const HOLD_MS = 300; // 原版第一段 DelayModifier 0.3s
const FADE_OUT_MS = 300; // 原版第二段 FadeOutModifier 0.3s

/**
 * 捨牌門前預告。每家最多同時一個提示。
 */
export class CDiscardAnnounce {
  private readonly sprites: Map<TSeat, CTileSprite> = new Map();

  constructor(private readonly scene: Phaser.Scene) {}

  /**
   * 在 seat 門前顯示要打出的正立放大牌；停留後淡出。
   * @param onLeave 淡出完成時呼叫（caller 在此把牌放進海底，延續原版時序）。
   */
  show(seat: TSeat, tile: TTile, onLeave?: () => void): void {
    const sprite = this.createSprite(seat, tile);
    this.scene.time.delayedCall(HOLD_MS, () => {
      if (!sprite.active) return;
      this.fadeOut(seat, sprite, onLeave);
    });
  }

  /** 等待其他家反應時持續顯示，不自動淡出或進海底。 */
  showPending(seat: TSeat, tile: TTile): void {
    this.createSprite(seat, tile);
  }

  /**
   * 無人取用捨牌：開始淡出門前牌並把牌交給海底。
   * 若目前沒有門前牌（一般快速出牌路徑），回傳 false 讓 caller 自行處理。
   */
  commitToPool(seat: TSeat, onLeave: () => void): boolean {
    const sprite = this.sprites.get(seat);
    if (!sprite) return false;
    this.fadeOut(seat, sprite, onLeave);
    return true;
  }

  /** 捨牌被吃／碰／槓取用：淡出門前牌，不放入海底。 */
  dismiss(seat: TSeat): void {
    const sprite = this.sprites.get(seat);
    if (!sprite) return;
    this.fadeOut(seat, sprite);
  }

  /** 建立一張原版正立放大門前牌。 */
  private createSprite(seat: TSeat, tile: TTile): CTileSprite {
    this.clearSeat(seat);

    const presentation = discardAnnouncePresentationForSeat(seat);
    const sprite = new CTileSprite(
      this.scene,
      presentation.x,
      presentation.y,
      tile,
      TileFace.HandBottom
    );
    sprite.setScale(ANNOUNCE_SCALE).setDepth(ANNOUNCE_DEPTH).setAlpha(1);
    this.sprites.set(seat, sprite);
    return sprite;
  }

  /** 淡出指定 sprite；若期間已被新預告取代，不會誤刪新 sprite。 */
  private fadeOut(seat: TSeat, sprite: CTileSprite, onComplete?: () => void): void {
    this.scene.tweens.killTweensOf(sprite);
    this.scene.tweens.add({
      targets: sprite,
      alpha: 0,
      duration: FADE_OUT_MS,
      ease: 'Quad.easeIn',
      onComplete: () => {
        sprite.destroy();
        if (this.sprites.get(seat) === sprite) this.sprites.delete(seat);
        onComplete?.();
      },
    });
  }

  /** 立即清掉某家的預告（含進行中的 tween）。 */
  private clearSeat(seat: TSeat): void {
    const existing = this.sprites.get(seat);
    if (existing) {
      this.scene.tweens.killTweensOf(existing);
      existing.destroy();
      this.sprites.delete(seat);
    }
  }

  /** 清掉全部預告（回合切換 / 場景重置用）。 */
  clear(): void {
    for (const seat of [...this.sprites.keys()]) this.clearSeat(seat);
  }
}
